// Compact trade history table. Reuses the TradeCaseInput shape expected by
// the chart layer — accepts trade cases as a prop so the parent (TradeAuditSection)
// owns fetching and can share the list with TradeAuditCharts.
interface TradeCaseInput {
	id: number;
	pair: string | null;
	direction: string | null;
	currentState: string;
	outcome: string | null;
	openedAt: string | null;
	closedAt: string | null;
	entryPriceFilled: string | null;
	profitLossMoney: string | null;
	profitLossPercent: string | null;
	tpHitsCount: number;
	tpTargetCount: number;
}

interface TradeHistoryTableProps {
	tradeCases: TradeCaseInput[];
	limit?: number;
}

function formatDate(iso: string | null): string {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString();
	} catch {
		return iso;
	}
}

function outcomeClassFor(outcome: string | null): string {
	if (!outcome) return "tg-audit-tone-muted";
	const o = outcome.toUpperCase();
	if (o === "COMPLETED") return "tg-audit-tone-success";
	if (o === "CLOSED") return "tg-audit-tone-info";
	if (o === "CANCELED") return "tg-audit-tone-warning";
	if (o === "FAILED") return "tg-audit-tone-danger";
	return "tg-audit-tone-muted";
}

export const TradeHistoryTable = ({
	tradeCases,
	limit = 50,
}: TradeHistoryTableProps) => {
	if (tradeCases.length === 0) return null;

	// Sort by closedAt desc (fallback to openedAt). Executed trades surface first.
	const sorted = [...tradeCases]
		.sort((a, b) => {
			const ta =
				Date.parse(a.closedAt ?? a.openedAt ?? "") ||
				Number.NEGATIVE_INFINITY;
			const tb =
				Date.parse(b.closedAt ?? b.openedAt ?? "") ||
				Number.NEGATIVE_INFINITY;
			return tb - ta;
		})
		.slice(0, limit);

	return (
		<div className="tg-surface-soft mt-4 overflow-hidden rounded-2xl">
			<header className="flex items-center justify-between border-b border-[color:var(--color-tg-line)] px-4 py-2.5">
				<h4 className="tg-text-primary text-[0.9rem] font-semibold leading-5">
					Recent trades
				</h4>
				<span className="tg-text-muted text-[0.8rem]">
					Showing {sorted.length} of {tradeCases.length}
				</span>
			</header>
			<div className="tg-scrollbar max-h-[360px] overflow-auto">
				<table className="w-full text-left text-[0.85rem]">
					<thead className="sticky top-0 bg-[color:var(--color-tg-pane)]">
						<tr className="tg-text-muted text-[0.75rem] uppercase tracking-[0.08em]">
							<th className="px-4 py-2 font-semibold">Pair</th>
							<th className="px-4 py-2 font-semibold">Dir</th>
							<th className="px-4 py-2 font-semibold">State</th>
							<th className="px-4 py-2 font-semibold">Outcome</th>
							<th className="px-4 py-2 text-right font-semibold">P/L</th>
							<th className="px-4 py-2 text-right font-semibold">TPs</th>
							<th className="px-4 py-2 font-semibold">Closed</th>
						</tr>
					</thead>
					<tbody>
						{sorted.map((t) => (
							<tr
								key={t.id}
								className="border-t border-[color:var(--color-tg-line)]"
							>
								<td className="tg-text-primary px-4 py-2 font-semibold">
									{t.pair ?? "—"}
								</td>
								<td className="tg-text-secondary px-4 py-2">
									{t.direction ?? "—"}
								</td>
								<td className="tg-text-secondary px-4 py-2">
									{t.currentState}
								</td>
								<td className="px-4 py-2">
									{t.outcome ? (
										<span
											className={`inline-flex rounded-lg border px-2 py-0.5 text-[0.72rem] font-semibold ${outcomeClassFor(t.outcome)}`}
										>
											{t.outcome}
										</span>
									) : (
										<span className="tg-text-muted">—</span>
									)}
								</td>
								<td className="tg-text-primary px-4 py-2 text-right font-mono text-[0.82rem]">
									{t.profitLossMoney ?? t.profitLossPercent ?? "—"}
								</td>
								<td className="tg-text-secondary px-4 py-2 text-right">
									{t.tpHitsCount}/{t.tpTargetCount}
								</td>
								<td className="tg-text-muted px-4 py-2 text-[0.8rem]">
									{formatDate(t.closedAt ?? t.openedAt)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
